// src/Components/CreateNewUserModal.jsx
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
 Button,
 Modal,
 Box,
 Typography,
 TextField,
 MenuItem,
 InputLabel,
 Select,
 FormControl,
 Grid,
 InputAdornment,
} from "@mui/material";
import { generateFirestoreAutoId } from '../Contexts/utilFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { addClient } from '../Redux/slices/clientSlice';
import { isOrganizationDuplicate } from '../utils/getClientDetails';
import { 
  COUNTRY_OPTIONS, 
  CURRENCY_OPTIONS,
  DROPDOWN_FIELDS,      
  INITIAL_CLIENT_STATE, 
  API_ENDPOINTS,
  MODAL_STYLES,
  INPUT_STYLES,
  COUNTRY_SELECTOR_STYLES
} from '../utils/constants';
import { prepareCollaboratorPayload } from '../utils/formHelpers';




/**
 * CreateNewUserModal - Enterprise-grade modal component for creating and editing client/collaborator data
 * Features comprehensive validation, error handling, and accessibility compliance
 * @param {Object} props - Component props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Modal close handler
 * @param {Array} props.clients - Array of existing clients for duplicate checking
 * @param {Function} props.setClients - Function to update parent clients state
 * @param {string} props.headerText - Modal header text
 * @param {string} props.type - Type of collaborator ('Client_Collaborator', 'User_Collaborator', etc.)
 * @param {Object} props.currentClient - Current client context
 * @param {Object} props.editClientData - Data for editing existing client
 * @param {string} props.collaborator_id - Collaborator ID
 * @param {Object} props.collaborator - Collaborator data
 * @param {boolean} props.isFirstTime - First-time user flag
 */
function CreateNewUserModal({
  open,
  onClose,
  clients,
  setClients,
  headerText,
  type,
  currentClient,
  editClientData,
  collaborator_id,
  collaborator,
  isFirstTime //  Accept this new prop from the dashboard
}) {
  const dispatch = useDispatch();
 
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('US');
  const [currency, setCurrency] = useState('USD');


 // initialize client_id once
 const [clientData, setClientData] = useState({
   client_id: generateFirestoreAutoId(),
   name: collaborator ? collaborator.contact_person : "",
   organizationName: collaborator?.organization_name || "",
   email: collaborator ? collaborator.email : "",
   phone: collaborator ? collaborator.phone : "",
   website: collaborator ? collaborator.website : "",
   companyRevenue: collaborator?.companyRevenue || "",
   employeeCount: collaborator?.employeeCount || "",
   foundedYear: collaborator?.foundedYear || "",
   proposals: collaborator?.proposals || {},
   // dropdown fields
   gender: collaborator?.gender || '',
   businessType: collaborator?.businessType || '',
   veteranStatus: collaborator?.veteranStatus || '',
   disabled: collaborator?.disabled || '',
 });


 const baseurl = process.env.REACT_APP_API_URL;


 // Watch for changes in collaborator, currentClient, or editClientData to populate the form
 useEffect(() => {
   const sourceData = editClientData || collaborator;

   if (sourceData) {
     setClientData({
       client_id: sourceData.client_id || sourceData.collaborator_id || generateFirestoreAutoId(),
       name: sourceData.contact_person || "",
       organizationName: sourceData.organization_name || "",
       email: sourceData.email || "",
       phone: sourceData.phone || "",
       website: sourceData.website || "",
       companyRevenue: sourceData.companyRevenue || "",
       employeeCount: sourceData.employeeCount || "",
       foundedYear: sourceData.foundedYear || "",
       proposals: sourceData.proposals || {},
       gender: sourceData.gender || '',
       businessType: sourceData.businessType || '',
       veteranStatus: sourceData.veteranStatus || '',
       disabled: sourceData.disabled || '',
     });
   } else if (currentClient?.organization_name) {
     // In create mode under a specific client, pre-fill organization name only
     setClientData(prev => ({ ...prev, organizationName: currentClient.organization_name }));
   }
 }, [currentClient, collaborator, editClientData]);


 /**
  * Handles form input changes with validation
  * @param {Event} e - Change event
  */
 const handleChange = useCallback((e) => {
   const { name, value } = e.target;
   setClientData(prev => ({ ...prev, [name]: value }));
   if (name === 'organizationName') setError(null);
 }, []);


 /**
  * Handles saving existing collaborator data
  * Performs validation, API call, and state updates
  */
 const handleSave = async () => {
  setLoading(true);
  try {
    // 1. Use the tool to clean all the data at once
    const cleanedData = prepareCollaboratorPayload(clientData, countryCode, COUNTRY_OPTIONS, collaborator);

    const payload = {
      type: type,
      user_id: localStorage.getItem("user_id"),
      collaborator_id: collaborator_id,
      collaborator_data: cleanedData, // Much cleaner!
    };

    const response = await fetch(`${baseurl}${API_ENDPOINTS.EDIT_COLLABORATOR}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });


     if (!response.ok) {
       const errTxt = await response.text();
       throw new Error(errTxt || 'Failed to save collaborator');
     }


     const result = await response.json();


     // Dispatch updated client data to Redux if this is a client org creation flow
     if (!type) {
       dispatch(addClient({
         client_id: clientData.client_id,
         contact_person: clientData.name,
         organization_name: clientData.organizationName,
         email: clientData.email,
         phone: cleanedData.phone,
         website: clientData.website,
         proposals: clientData.proposals || {},
         companyRevenue: Number(clientData.companyRevenue || 0),
         employeeCount: Number(clientData.employeeCount || 0),
         foundedYear: Number(clientData.foundedYear || 0),
         gender: clientData.gender || '',
         businessType: clientData.businessType || '',
         veteranStatus: clientData.veteranStatus || '',
         disabled: clientData.disabled || '',
       }));
     }


     // If this is collaborator flow (type truthy), add to parent collaborators list
     if (type && typeof setClients === 'function') {
       setClients(prev => [
         ...prev,
         {
           contact_person: clientData.name,
           email: clientData.email,
           phone: cleanedData.phone,
           organization_name: clientData.organizationName,
           companyRevenue: clientData.companyRevenue,
           employeeCount: clientData.employeeCount,
           foundedYear: clientData.foundedYear,
           gender: clientData.gender,
           businessType: clientData.businessType,
           veteranStatus: clientData.veteranStatus,
           disabled: clientData.disabled,
         }
       ]);
     }


     setError(null);
     onClose();
   } catch (err) {
     console.error('Edit collaborator error:', err);
     setError('Failed to save collaborator. Please try again.');
   } finally {
     setLoading(false);
   }
 };


 const handleCreateClient = async () => {
   // validation
   if (type === 'User_Collaborator') {
     if (!clientData.name || !clientData.email) {
       alert("Please fill in all required fields.");
       return;
     }
   } else {
     if (!clientData.name || !clientData.email || (!type && !clientData.organizationName)) {
       alert("Please fill in all required fields.");
       return;
     }
   }


   setLoading(true);
   try {
    // 1. Use the tool again!
    const cleanedData = prepareCollaboratorPayload(clientData, countryCode, COUNTRY_OPTIONS);

    const payload = {
      type: type || "Client_Organization",
      user_id: localStorage.getItem("user_id"),
      collaborator_id: clientData.client_id,
      collaborator_data: cleanedData,
    };

    const response = await fetch(`${baseurl}${API_ENDPOINTS.ADD_COLLABORATOR}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });


     if (!response.ok) {
       const errTxt = await response.text();
       throw new Error(errTxt || 'Failed to add collaborator');
     }


     const result = await response.json();


     // Update Redux store for main client list
     if (!type) {
       dispatch(addClient({
         client_id: clientData.client_id,
         contact_person: clientData.name,
         organization_name: clientData.organizationName,
         email: clientData.email,
         phone: cleanedData.phone,
         website: clientData.website,
         proposals: clientData.proposals || {},
         companyRevenue: Number(clientData.companyRevenue || 0),
         employeeCount: Number(clientData.employeeCount || 0),
         foundedYear: Number(clientData.foundedYear || 0),
         gender: clientData.gender || '',
         businessType: clientData.businessType || '',
         veteranStatus: clientData.veteranStatus || '',
         disabled: clientData.disabled || '',
       }));
     }


     // update parent collaborators list when creating collaborator
     if (type && typeof setClients === 'function') {
       setClients(prev => [
         ...prev,
         {
           contact_person: clientData.name,
           email: clientData.email,
           phone: cleanedData.phone,
           organization_name: clientData.organizationName,
           companyRevenue: clientData.companyRevenue,
           employeeCount: clientData.employeeCount,
           foundedYear: clientData.foundedYear,
           gender: clientData.gender,
           businessType: clientData.businessType,
           veteranStatus: clientData.veteranStatus,
           disabled: clientData.disabled,
         }
       ]);
     }


     // reset form using our new constant
      setClientData({
        ...INITIAL_CLIENT_STATE,
        client_id: generateFirestoreAutoId(),
      });


     setError(null);
     onClose();
   } catch (err) {
     console.error('Create collaborator error:', err);
     setError('Failed to create client. Please try again.');
   } finally {
     setLoading(false);
   }
 };



 const handlePrimaryButtonClick = () => {
   if (type === 'Client_Collaborator' && collaborator_id) {
     if (!clientData.name || !clientData.email || (!type && !clientData.organizationName)) {
       alert("Please fill in all required fields.");
       return;
     }
     handleSave();
     return;
   }


   if (type === 'User_Collaborator') {
     handleCreateClient();
     return;
   }


   // default Client_Organization flow
   if (clientData && clientData.organizationName) {
     // We pass 'clients' and the name we want to check into the tool
       const organizationExists = isOrganizationDuplicate(clients, clientData.organizationName);
     if (organizationExists && !currentClient) {
       setError('Organization Already Exists!');
       return;
     } else {
       handleCreateClient();
     }
   } else {
     alert("Please fill in all required fields.");
   }
 };


 const modalStyle = MODAL_STYLES;


 const customStyle = INPUT_STYLES;


 return (
   <Modal open={open} onClose={onClose} aria-labelledby="modal-title" aria-describedby="modal-description">
     <Box sx={modalStyle}>
       {error && (<p className='text-center font-bold text-white text-[20px]'>{error}</p>)}
       {/* Welcome message for first-time users */}
       {isFirstTime && (
    <div style={{ textAlign: 'center', marginBottom: '20px', padding: '10px' }}>
      <Typography variant="h6" color="white">Welcome to GrantAlde!</Typography>
      <Typography variant="body2" color="#CDCDCD">
        It looks like you're new here. Create your first client to get started.
      </Typography>
    </div>
  )}

       <Typography sx={{ display: "flex", justifyContent: "center", marginBottom: '1px'}} id="modal-title" variant="h6" component="h2" color="white">
         {collaborator ? "Edit Client" : "New Client Details"}
       </Typography>

       <Grid container spacing={2} sx={{ mt: 1 }}>
         <Grid item xs={12} sm={6}>
           <TextField
             id="name"
             label="Client Name"
             name="name"
             value={clientData.name || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             required
             sx={{ ...customStyle }}
           />
         </Grid>

         <Grid item xs={12} sm={6}>
           {/* Single organization-name field: read-only when Client_Collaborator and currentClient exists */}
           <TextField
             id="organization-name"
             label="Organization Name"
             name="organizationName"
             value={type === 'Client_Collaborator' && currentClient ? (currentClient.organization_name || '') : (clientData.organizationName || '')}
             onChange={type === 'Client_Collaborator' && currentClient ? undefined : handleChange}
             variant="outlined"
             fullWidth
             required
             InputProps={{
               readOnly: type === 'Client_Collaborator' && currentClient ? true : false,
             }}
             sx={{ ...customStyle }}
           />
         </Grid>

         <Grid item xs={12} sm={6}>
           <TextField
             id="email"
             label="Email Address"
             name="email"
             type="email"
             value={clientData.email || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             required
             sx={{ ...customStyle }}
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             id="phone"
             label="Phone Number"
             name="phone"
             value={clientData.phone || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             sx={{ ...customStyle }}
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <Select
                     value={countryCode}
                     onChange={(e) => setCountryCode(e.target.value)}
                     variant="standard"
                     disableUnderline
                     sx={COUNTRY_SELECTOR_STYLES}
                   >
                     {COUNTRY_OPTIONS.map((option) => (
                       <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                     ))}
                   </Select>
                 </InputAdornment>
               ),
             }}
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             id="website"
             label="Website"
             name="website"
             value={clientData.website || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             sx={{ ...customStyle }}
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             id="founded-year"
             label="Founded Year"
             name="foundedYear"
             type="number"

             value={clientData.foundedYear || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             sx={{ ...customStyle }}
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             id="company-revenue"
             label="Company Revenue"
             name="companyRevenue"
             type="number"
             value={clientData.companyRevenue || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             sx={{ ...customStyle }}
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <Select
                     value={currency}
                     onChange={(e) => setCurrency(e.target.value)}
                     variant="standard"
                     disableUnderline
                     sx={COUNTRY_SELECTOR_STYLES}
                   >
                     {CURRENCY_OPTIONS.map((option) => (
                       <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                     ))}
                   </Select>
                 </InputAdornment>
               ),
             }}
           />
         </Grid>
         <Grid item xs={12} sm={6}>
           <TextField
             id="employee-count"
             label="Employee Count"
             name="employeeCount"
             type="number"
             value={clientData.employeeCount || ''}
             onChange={handleChange}
             variant="outlined"
             fullWidth
             sx={{ ...customStyle }}
           />
         </Grid>

         {/* Dropdowns (no 'None' option) */}
         {DROPDOWN_FIELDS.map((field) => (
           <Grid item xs={12} sm={6} key={field.name}>
             <FormControl fullWidth sx={{ ...customStyle }}>
               <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
               <Select
                 labelId={`${field.name}-label`}
                 id={field.name}
                 name={field.name}
                 value={clientData[field.name] || ''}
                 label={field.label}
                 onChange={handleChange}
               >
                 {field.options.map((option, idx) => (
                   <MenuItem key={`${option.value}-${idx}`} value={option.value}>
                     {option.label}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
           </Grid>
         ))}
       </Grid>

       <div className="file-upload-btns">
         <Button
           onClick={handlePrimaryButtonClick}
           variant="contained"
           color="primary"
           sx={{ mt: 3 }}
           disabled={loading}
         >
           {collaborator ? 'Edit Client' : 'Create'}
         </Button>


         <Button
            onClick={() => {
              onClose();
              // reset form using our new constant
              setClientData({
                ...INITIAL_CLIENT_STATE,
                client_id: generateFirestoreAutoId(),
              });
              setError(null);
            }}
           variant="contained"
           color="primary"
           // added 'isFirstTime' to the disabled check
           disabled={loading || isFirstTime}
           sx={{ 
             mt: 3, 
             ml: 1,
             "&.Mui-disabled": {
                 backgroundColor: "rgba(255, 255, 255, 0.12)", // Visible light grey
                 color: "rgba(255, 255, 255, 0.3)", 
                 cursor: "not-allowed !important",
                 pointerEvents: "auto !important"         
         },
            
              cursor: isFirstTime ? 'not-allowed' : 'pointer',
              pointerEvents: 'auto' // Ensures the cursor shows even when disabled
          }}
>
           Cancel
         </Button>
       </div>
     </Box>
   </Modal>
 );
}


CreateNewUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clients: PropTypes.array,
  setClients: PropTypes.func,
  headerText: PropTypes.string,
  type: PropTypes.string,
  currentClient: PropTypes.object,
  editClientData: PropTypes.object,
  collaborator_id: PropTypes.string,
  collaborator: PropTypes.object,
  isFirstTime: PropTypes.bool
};

CreateNewUserModal.defaultProps = {
  clients: [],
  setClients: null,
  headerText: '',
  type: null,
  currentClient: null,
  editClientData: null,
  collaborator_id: '',
  collaborator: null,
  isFirstTime: false
};

export default CreateNewUserModal;
